import React, { useRef } from "react";
import slugify from "@/utils/functions/slugify";
import { QuizOption } from "../type";
import { OptionButton } from "./OptionButton";

/**
 * Renders quiz option selections. Supports both single-select (radio)
 * and multi-select (button) modes.
 *
 * NOTE: If adding more selection types beyond these two, consider
 * refactoring into separate components to maintain simplicity.
 */
interface OptionsContainerProps {
  options: QuizOption[];
  selectedOptions: string[];
  variant: "basic" | "card";
  showAllOptions: boolean;
  onToggle: (value: string, label: string) => void;
  selectionType?: "single" | "multiple";
  sectionTitle?: string;
}

const getVisibleOptions = (
  options: QuizOption[],
  showAllOptions: boolean,
): QuizOption[] => {
  const shouldLimit = options.length > 10 && !showAllOptions;
  return shouldLimit ? options.slice(0, 10) : options;
};

const getOptionValue = (option: QuizOption): string => {
  return option.searchFilterValue || option.value || option.label;
};

const getOptionStyling = (isBasic: boolean, isSelected: boolean): string => {
  const baseStyles = "not-prose transition-all";
  const variantStyles = isBasic
    ? "w-fit py-2 px-5 rounded-full"
    : "flex flex-col p-6 rounded-xl justify-center shadow-xl w-full md:flex-1 md:min-w-0";
  const selectionStyles =
    isSelected && isBasic
      ? "bg-primary-400 text-white"
      : isSelected && !isBasic
        ? "bg-white border border-2 border-brand-500"
        : "bg-white text-gray-900";
  return `${baseStyles} ${variantStyles} ${selectionStyles}`;
};

export const OptionsContainer: React.FC<OptionsContainerProps> = ({
  options,
  selectedOptions,
  variant,
  showAllOptions,
  onToggle,
  selectionType = "multiple",
  sectionTitle,
}) => {
  const radioGroupRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLElement | null)[]>([]);

  if (options.length === 0) {
    return (
      <div className="text-gray-600 py-4">
        No options available for this category.
      </div>
    );
  }

  const visibleOptions = getVisibleOptions(options, showAllOptions);
  const isBasic = variant === "basic";
  const groupName = sectionTitle
    ? `quiz-${slugify(sectionTitle)}`
    : "quiz-options";

  const handleRadioGroupKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (selectionType !== "single") return;

    const isArrowKey =
      e.key === "ArrowDown" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowLeft";

    if (!isArrowKey) return;

    e.preventDefault();

    const radioButtons = radioGroupRef.current?.querySelectorAll(
      '[role="radio"]',
    ) as NodeListOf<HTMLElement>;
    if (!radioButtons || radioButtons.length === 0) return;

    const currentIndex = Array.from(radioButtons).findIndex(
      (radio) => radio === document.activeElement,
    );

    let nextIndex: number;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % radioButtons.length;
    } else {
      nextIndex =
        currentIndex <= 0 ? radioButtons.length - 1 : currentIndex - 1;
    }

    radioButtons[nextIndex].focus();
  };

  const handleMultiOptionKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (selectionType !== "multiple") return;

    const isArrowKey =
      e.key === "ArrowDown" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowLeft";

    if (!isArrowKey) return;

    const currentIndex = optionRefs.current.findIndex(
      (element) => element === e.currentTarget,
    );

    if (currentIndex === -1 || optionRefs.current.length <= 1) {
      return;
    }

    e.preventDefault();

    const direction = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : -1;
    const total = optionRefs.current.length;
    let nextIndex = currentIndex;

    for (let attempts = 0; attempts < total; attempts += 1) {
      nextIndex = (nextIndex + direction + total) % total;
      const nextElement = optionRefs.current[nextIndex];
      if (nextElement) {
        nextElement.focus();
        break;
      }
    }
  };

  // For radio buttons, determine which option should be tabbable
  const selectedIndex =
    selectedOptions.length > 0
      ? visibleOptions.findIndex((option) =>
          selectedOptions.includes(getOptionValue(option)),
        )
      : -1;
  const hasValidSelection = selectedIndex >= 0;

  optionRefs.current.length = visibleOptions.length;

  const contentComponent = visibleOptions.map((option, index) => {
    const optionValue = getOptionValue(option);
    const isSelected = selectedOptions.includes(optionValue);
    const optionStyling = getOptionStyling(isBasic, isSelected);

    // For single selection (radio), only one option should be tabbable:
    // - If there's a selection, only that option is tabbable
    // - If no selection, only the first option is tabbable
    const isTabbable =
      selectionType === "single"
        ? hasValidSelection
          ? index === selectedIndex
          : index === 0
        : true;

    return (
      <OptionButton
        key={optionValue}
        optionValue={optionValue}
        optionLabel={option.label}
        isSelected={isSelected}
        isBasic={isBasic}
        optionStyling={optionStyling}
        icon={option.icon}
        description={option.description}
        onToggle={onToggle}
        selectionType={selectionType}
        groupName={groupName}
        onKeyDown={
          selectionType === "single"
            ? handleRadioGroupKeyDown
            : handleMultiOptionKeyDown
        }
        index={index}
        isTabbable={isTabbable}
        setRef={(element) => {
          optionRefs.current[index] = element;
        }}
      />
    );
  });

  if (selectionType === "single") {
    return (
      <div
        ref={radioGroupRef}
        role="radiogroup"
        aria-label={sectionTitle}
        className="contents"
      >
        {contentComponent}
      </div>
    );
  }

  return <>{contentComponent}</>;
};
